/**
 * Состояния карточки МП для вкладки просмотра.
 *
 * `state` считает design-service; здесь только подписи и то, как состояние
 * попадает в сводку. Разделение важное: «не уедет» (блокирующая причина) и
 * «уедет с пустым полем» (пробел) — разные вещи, и красным помечается только
 * первое.
 */

import type { BadgeVariant } from '../../../../components/ui/Badge'
import { assertNever } from '../../../../shared/marketplaces'
import type { MarketplaceCardDto, MarketplaceCardState } from '../../../pipeline/api'

interface StateText {
  label: string
  variant: BadgeVariant
}

const STATE: Record<MarketplaceCardState, StateText> = {
  live: { label: 'На витрине', variant: 'green' },
  live_no_photo: { label: 'На витрине, без фото', variant: 'yellow' },
  commercial_pim: { label: 'Цена только в PIM', variant: 'yellow' },
  error: { label: 'Ошибка площадки', variant: 'red' },
  frozen: { label: 'Состав заморожен', variant: 'gray' },
  no_revision: { label: 'Ревизия не найдена', variant: 'red' },
}

/**
 * Состояние карточки, пришедшей без поля `state`: сервис мог остаться старой
 * версии. Выводим по тем же правилам, что и бэкенд, чтобы вкладка не осталась
 * без подписи.
 */
export function cardState(card: MarketplaceCardDto): MarketplaceCardState {
  if (card.state) return card.state
  if (card.status === 'error') return 'error'
  /**
   * `selling` без явного состояния — историческая отметка PIM, а не витрина:
   * так помечены строки каркаса Этапа 6, и внешнее состояние карточки по ним не
   * подтверждено. Живой строке синхронизированного каталога бэкенд всегда
   * присылает `state`, поэтому досчитывать «на витрине» здесь нельзя —
   * получилось бы утверждение о продажах, которого никто не проверял.
   */
  if (card.status === 'price_set' || card.status === 'stock_set' || card.status === 'selling') {
    return 'commercial_pim'
  }
  return card.revision_snapshot ? 'frozen' : 'no_revision'
}

export const stateText = (card: MarketplaceCardDto): StateText => STATE[cardState(card)]

/** Причины, из-за которых карточка на площадку не уедет. */
export const blockingCodes = (card: MarketplaceCardDto): string[] =>
  card.revision_snapshot?.readiness?.blocking ?? []

/** Пробелы: карточка уедет, но поле на площадке останется пустым. */
export const gapCodes = (card: MarketplaceCardDto): string[] =>
  card.revision_snapshot?.readiness?.gaps ?? []

/** Карточка не уедет: либо состояние само по себе поломанное, либо есть блокировка. */
export function isBlocked(card: MarketplaceCardDto): boolean {
  const state = cardState(card)
  return state === 'error' || state === 'no_revision' || blockingCodes(card).length > 0
}

/** Карточка уедет, но с замечанием, которое человеку стоит увидеть. */
export function hasWarning(card: MarketplaceCardDto): boolean {
  if (isBlocked(card)) return false
  const state = cardState(card)
  return state === 'live_no_photo' || state === 'commercial_pim' || gapCodes(card).length > 0
}

/** Бейдж строки: блокировка важнее исходного состояния. */
export function rowBadge(card: MarketplaceCardDto): StateText {
  const base = stateText(card)
  if (isBlocked(card) && blockingCodes(card).length > 0 && base.variant !== 'red') {
    return { label: 'Не уедет на площадку', variant: 'red' }
  }
  if (hasWarning(card) && gapCodes(card).length > 0 && base.variant === 'gray') {
    return { label: 'Уедет с пустыми полями', variant: 'yellow' }
  }
  return base
}

/**
 * Изображения карточки. У живой строки это фото витрины, у замороженной —
 * манифест ревизии: то, что уедет на площадку.
 */
export function cardPhotos(card: MarketplaceCardDto): string[] {
  const live = card.image_urls?.filter(Boolean) ?? []
  if (live.length) return live
  if (card.preview_url) return [card.preview_url]
  const manifest = card.revision_snapshot?.asset_manifest
  const urls = manifest && Array.isArray((manifest as any).urls) ? (manifest as any).urls : []
  return urls.filter((url: unknown): url is string => typeof url === 'string' && !!url.trim())
}

/**
 * Цена: у замороженной карточки — та, что одобрили, а не текущая из справочника.
 * Правка справочника после одобрения на уехавший состав не влияет, и показывать
 * её как цену карточки нельзя.
 */
export function cardPrice(card: MarketplaceCardDto): number | null {
  const frozen = card.revision_snapshot?.commercials
  const price = frozen && typeof (frozen as any).price === 'number' ? (frozen as any).price : null
  if (cardState(card) === 'frozen' && price !== null) return price
  return card.price ?? price
}

export function cardStock(card: MarketplaceCardDto): number | null {
  const frozen = card.revision_snapshot?.commercials
  const stock = frozen && typeof (frozen as any).stock === 'number' ? (frozen as any).stock : null
  return card.stock ?? stock
}

/**
 * Ссылка на карточку площадки; пусто, пока карточки на витрине нет.
 *
 * `switch` с `assertNever`, а не тернарник: раньше «не wb» молча уводило в
 * ветку OZON — для Я.Маркета это сгенерировало бы неверную ссылку на чужую
 * площадку. Формат витрины Маркета не подтверждён (решение владельца,
 * `docs/SPEC_2026-09-13_YANDEX_MARKET.md`, §5), поэтому у ym ссылки нет вовсе.
 */
export function storefrontUrl(card: MarketplaceCardDto): string | null {
  if (!card.external_id) return null
  const id = String(card.external_id).trim()
  if (!id || !/^\d+$/.test(id)) return null
  switch (card.marketplace) {
    case 'wb':
      return `https://www.wildberries.ru/catalog/${id}/detail.aspx`
    case 'ozon':
      return `https://www.ozon.ru/product/${id}`
    case 'ym':
    case 'site':
      return null
    default:
      return assertNever(card.marketplace)
  }
}
